"use client";

import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api";
import { radiusQuote } from "@/lib/food-online";

export const DELIVERY_QUOTE_TIMEOUT_MS = 15000;
const DEBOUNCE_MS = 700;
const QUOTE_REUSE_MS = 60000;

type QuoteState =
  | { key: string; status: "idle" | "loading" }
  | { key: string; status: "ready"; fee: string }
  | { key: string; status: "error"; error: string };

function quoteErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if (error.data && typeof error.data === "object") {
      for (const value of Object.values(error.data)) {
        if (Array.isArray(value) && typeof value[0] === "string") return value[0];
        if (typeof value === "string") return value;
      }
    }
    return error.message;
  }
  return "Não foi possível calcular a entrega agora. Confira o endereço e tente novamente.";
}

export function useDeliveryQuote(slug: string, addressKey: string, enabled: boolean, complete: boolean, rulesKey = "") {
  const quotes = useRef(new Map<string, { fee: string; expires: number }>());
  const cacheKey = JSON.stringify([slug, rulesKey, addressKey]);
  const [state, setState] = useState<QuoteState>({ key: cacheKey, status: "idle" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !complete) {
      setState({ key: cacheKey, status: "idle" });
      return;
    }

    const cached = quotes.current.get(cacheKey);
    if (cached && cached.expires > Date.now()) {
      setState({ key: cacheKey, status: "ready", fee: cached.fee });
      return;
    }

    let active = true;
    let timedOut = false;
    const controller = new AbortController();
    let deadline: ReturnType<typeof setTimeout> | undefined;
    setState({ key: cacheKey, status: "loading" });
    const debounce = setTimeout(() => {
      deadline = setTimeout(() => {
        timedOut = true;
        controller.abort();
        if (active) setState({ key: cacheKey, status: "error",
          error: "A consulta de entrega demorou mais que o esperado. Tente validar o endereço novamente." });
      }, DELIVERY_QUOTE_TIMEOUT_MS);
      void radiusQuote(slug, JSON.parse(addressKey) as Record<string, string>, controller.signal)
        .then(value => {
          if (!active || timedOut) return;
          if (typeof value.fee !== "string" || value.fee.trim() === "" || !Number.isFinite(Number(value.fee)) || Number(value.fee) < 0) {
            throw new Error("Invalid delivery quote");
          }
          for (const [key, quote] of quotes.current) {
            if (quote.expires <= Date.now()) quotes.current.delete(key);
          }
          if (quotes.current.size >= 8) quotes.current.clear();
          quotes.current.set(cacheKey, { fee: value.fee, expires: Date.now() + QUOTE_REUSE_MS });
          setState({ key: cacheKey, status: "ready", fee: value.fee });
        })
        .catch(error => {
          if (active && !timedOut) setState({ key: cacheKey, status: "error", error: quoteErrorMessage(error) });
        })
        .finally(() => clearTimeout(deadline));
    }, DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(debounce);
      clearTimeout(deadline);
      controller.abort();
    };
  }, [slug, addressKey, enabled, complete, attempt, cacheKey]);

  // Invalidate synchronously on input change, before the effect starts the next
  // request. Old responses/cleanup can never make a different address usable.
  const current = enabled && complete && state.key === cacheKey ? state : null;
  const ready = current?.status === "ready";
  const error = current?.status === "error" ? current.error : undefined;
  return {
    ready,
    pending: enabled && complete && !ready && !error,
    error,
    fee: current?.status === "ready" ? current.fee : undefined,
    retry() {
      quotes.current.delete(cacheKey);
      setState({ key: cacheKey, status: "loading" });
      setAttempt(value => value + 1);
    },
  };
}
